import React, { useEffect, useState } from 'react';
import { ReactComponent as CrownSvg } from '../../../../assets/svg/crown.svg';
import { formatDate } from '../../../../lib/utils';
import request from '../../../../lib/request';

const CANCEL_REASONS = [
  'Too expensive',
  'Not using enough',
  'Found a better alternative',
  'Not satisfied with the service',
  'Other',
];

const PAUSE_REASONS = ['I need a break'];

const CancelSubscriptionDialog = ({
  handleCancelSubscription,
  setShowCancelDialog,
  selectedReason,
  setSelectedReason,
  otherReason,
  setOtherReason,
}) => {
  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-2xl p-6 max-w-md w-full mx-4'>
        <div className='space-y-4'>
          <h3 className='text-lg font-bold text-gray-800'>
            Cancel Subscription
          </h3>
          <p className='text-sm text-gray-600'>
            Please select a reason for cancelling your subscription. This helps
            us improve our service.
          </p>

          <div className='space-y-2'>
            {CANCEL_REASONS.map((reason) => (
              <label
                key={reason}
                className='flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer'
              >
                <input
                  type='radio'
                  name='cancelReason'
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className='text-yellow-500 focus:ring-yellow-500'
                />
                <span className='text-sm'>{reason}</span>
              </label>
            ))}
          </div>

          {selectedReason === 'Other' && (
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder='Please tell us more...'
              className='w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
              rows={3}
            />
          )}

          <div className='flex gap-3 pt-4'>
            <button
              onClick={() => setShowCancelDialog(false)}
              className='flex-1 py-2 text-sm text-gray-600 border rounded-xl hover:bg-gray-50 transition-colors duration-200'
            >
              Back
            </button>
            <button
              onClick={handleCancelSubscription}
              className='flex-1 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors duration-200'
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PauseSubscriptionDialog = ({
  handlePauseSubscription,
  setShowPauseDialog,
  selectedReason,
  setSelectedReason,
  otherReason,
  setOtherReason,
}) => {
  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-2xl p-6 max-w-md w-full mx-4'>
        <div className='space-y-4'>
          <h3 className='text-lg font-bold text-gray-800'>
            Pause Subscription
          </h3>
          <p className='text-sm text-gray-600'>
            Please select a reason for pausing your subscription. This helps us
            improve our service.
          </p>

          <div className='space-y-2'>
            {PAUSE_REASONS.map((reason) => (
              <label
                key={reason}
                className='flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer'
              >
                <input
                  type='radio'
                  name='pauseReason'
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className='text-yellow-500 focus:ring-yellow-500'
                />
                <span className='text-sm'>{reason}</span>
              </label>
            ))}
          </div>

          {selectedReason === 'Other' && (
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder='Please tell us more...'
              className='w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
              rows={3}
            />
          )}

          <div className='flex gap-3 pt-4'>
            <button
              onClick={() => setShowPauseDialog(false)}
              className='flex-1 py-2 text-sm text-gray-600 border rounded-xl hover:bg-gray-50 transition-colors duration-200'
            >
              Back
            </button>
            <button
              onClick={handlePauseSubscription}
              className='flex-1 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors duration-200'
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResumeSubscriptionDialog = ({
  handleResumeSubscription,
  setShowResumeDialog,
}) => {
  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-2xl p-6 max-w-md w-full mx-4'>
        <h3 className='text-lg font-bold text-gray-800'>Resume Subscription</h3>
        <p className='mt-2 text-sm text-gray-600'>
          Are you sure you want to resume your subscription? You will be charged
          for the next billing period.
        </p>

        <div className='flex gap-3 pt-4'>
          <button
            onClick={() => setShowResumeDialog(false)}
            className='flex-1 py-2 text-sm text-gray-600 border rounded-xl hover:bg-gray-50 transition-colors duration-200'
          >
            Back
          </button>
          <button
            onClick={handleResumeSubscription}
            className='flex-1 py-2 text-sm text-white bg-yellow-500 rounded-xl hover:bg-yellow-600 transition-colors duration-200'
          >
            Resume Subscription
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageSubscriptionDialog = ({
  dialogRef,
  closeDialog,
  userInfo,
  refreshUser,
}) => {
  const [nextPaymentDate, setNextPaymentDate] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  useEffect(() => {
    const { payment } = userInfo;
    if (payment.length > 0) {
      const activatedAt = new Date(payment[0].activatedAt);
      let period;

      if (payment[0]?.plan) {
        if (payment[0].plan.period === 'MONTHLY') {
          period = 'MONTHLY';
        } else if (payment[0].plan.period === 'YEARLY') {
          period = 'YEARLY';
        }
      }
      if (payment[0]?.subscriptionType) {
        if (payment[0].subscriptionType === 'MONTHLY') {
          period = 'MONTHLY';
        }
        if (payment[0].subscriptionType === 'YEARLY') {
          period = 'YEARLY';
        }
      }

      let nextPayment = new Date(activatedAt);
      if (period === 'MONTHLY') {
        nextPayment.setMonth(nextPayment.getMonth() + 1);
      }
      if (period === 'YEARLY') {
        nextPayment.setFullYear(nextPayment.getFullYear() + 1);
      }
      setNextPaymentDate(formatDate(nextPayment));
    }
  }, [userInfo]);

  const handleCancelSubscription = async () => {
    if (!selectedReason) {
      alert('Please select a reason for cancellation');
      return;
    }

    if (selectedReason === 'Other' && !otherReason.trim()) {
      alert('Please provide your reason for cancellation');
      return;
    }

    const reason = selectedReason === 'Other' ? otherReason : selectedReason;

    try {
      const { result } = await request.post('/paypal/subscription/cancel', {
        subscriptionId: userInfo.payment[0].subscriptionId,
        reason,
      });
      await refreshUser();
      closeDialog();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription. Please try again later.');
    }
  };

  const handlePauseSubscription = async () => {
    if (!selectedReason) {
      alert('Please select a reason for pausing your subscription');
      return;
    }

    if (selectedReason === 'Other' && !otherReason.trim()) {
      alert('Please provide your reason for pausing your subscription');
      return;
    }

    const reason = selectedReason === 'Other' ? otherReason : selectedReason;

    try {
      const { result } = await request.post('/paypal/subscription/pause', {
        subscriptionId: userInfo.payment[0].subscriptionId,
        reason,
      });
      await refreshUser();
      closeDialog();
    } catch (error) {
      console.error('Failed to pause subscription:', error);
      alert('Failed to pause subscription. Please try again later.');
    }
  };

  const handleResumeSubscription = async () => {
    try {
      const { result } = await request.post('/paypal/subscription/resume', {
        subscriptionId: userInfo.payment[0].subscriptionId,
      });
      await refreshUser();
      closeDialog();
    } catch (error) {
      console.error('Failed to resume subscription:', error);
      alert('Failed to resume subscription. Please try again later.');
    }
  };

  const handleCancelSubscriptionFromBuymeacoffee = () => {
    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      window.open('https://buymeacoffee.com/davidpark/membership', '_blank');
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className='w-full max-w-md p-6 rounded-2xl backdrop:bg-black/50'
    >
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='bg-yellow-500/20 p-1.5 rounded-lg'>
              <CrownSvg className='size-4 text-yellow-500' />
            </div>
            <h2 className='text-lg font-bold text-gray-800'>
              Premium Subscription
            </h2>
          </div>
          <button
            onClick={closeDialog}
            className='text-gray-400 hover:text-gray-600 transition-colors'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Subscription Details */}
        <div className='space-y-4'>
          <div className='bg-gray-50 rounded-xl p-4 space-y-2 text-xs'>
            <div className='flex justify-between items-center'>
              <span className='text-gray-600'>Current Plan</span>
              <span className='font-medium text-yellow-500'>Premium</span>
            </div>
            {userInfo.paused ? (
              <div className='flex justify-between items-center'>
                <span className='text-gray-600'>Paused On</span>
                <span className='font-medium'>
                  {formatDate(new Date(userInfo.payment[0].updatedAt))}
                </span>
              </div>
            ) : (
              <div className='flex justify-between items-center'>
                <span className='text-gray-600'>Activated On</span>
                <span className='font-medium'>
                  {formatDate(new Date(userInfo.payment[0].activatedAt))}
                </span>
              </div>
            )}
            {userInfo.payment[0]?.cancelAt ? (
              <div className='flex justify-between items-center'>
                <span className='text-gray-600'>Ends On</span>
                <span className='font-medium text-red-600'>
                  {formatDate(new Date(userInfo.payment[0].cancelAt))}
                </span>
              </div>
            ) : null}
          </div>

          {/* Premium Benefits */}
          <div className='space-y-2'>
            <h3 className='text-sm font-bold text-gray-700'>
              Premium Benefits
            </h3>
            <div className='space-y-1'>
              <div className='flex items-center gap-2 text-sm text-gray-600'>
                <div className='w-1.5 h-1.5 rounded-full bg-yellow-500'></div>
                <span>Unlimited access to all features</span>
              </div>
              <div className='flex items-center gap-2 text-sm text-gray-600'>
                <div className='w-1.5 h-1.5 rounded-full bg-yellow-500'></div>
                <span>Priority support</span>
              </div>
              <div className='flex items-center gap-2 text-sm text-gray-600'>
                <div className='w-1.5 h-1.5 rounded-full bg-yellow-500'></div>
                <span>Exclusive challenges</span>
              </div>
              <div className='flex items-center gap-2 text-sm text-gray-600'>
                <div className='w-1.5 h-1.5 rounded-full bg-yellow-500'></div>
                <span>Unlimited AI-powered meal tracking</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className='space-y-4'>
          {userInfo.payment[0]?.cancelAt ? (
            <a
              href='mailto:support@thechamps.app'
              className='text-sm text-blue-600 mb-8 italic underline'
            >
              Need help?
            </a>
          ) : (
            <button
              // onClick={() => setShowCancelDialog(true)}
              onClick={handleCancelSubscriptionFromBuymeacoffee}
              className='py-2 text-xs text-red-600 rounded-xl underline'
            >
              Cancel Subscription
            </button>
          )}
          <div className='flex gap-2 w-full'>
            {/* <button
              onClick={() => {
                if (userInfo.paused) {
                  setShowResumeDialog(true);
                } else {
                  setShowPauseDialog(true);
                }
              }}
              className={`w-full py-2.5 text-sm text-gray-600 border rounded-xl hover:bg-gray-50 transition-colors duration-200 ${
                userInfo.paused && 'border-yellow-500 text-yellow-500 font-bold'
              }`}
            >
              {userInfo.paused ? 'Resume Subscription' : 'Pause Subscription'}
            </button> */}
            <button
              onClick={closeDialog}
              className='w-full py-2.5 text-sm text-gray-600 border rounded-xl hover:bg-gray-50 transition-colors duration-200 w-full'
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      {showCancelDialog && (
        <CancelSubscriptionDialog
          handleCancelSubscription={handleCancelSubscription}
          setShowCancelDialog={setShowCancelDialog}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          otherReason={otherReason}
          setOtherReason={setOtherReason}
        />
      )}

      {showPauseDialog && (
        <PauseSubscriptionDialog
          handlePauseSubscription={handlePauseSubscription}
          setShowPauseDialog={setShowPauseDialog}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          otherReason={otherReason}
          setOtherReason={setOtherReason}
        />
      )}

      {showResumeDialog && (
        <ResumeSubscriptionDialog
          handleResumeSubscription={handleResumeSubscription}
          setShowResumeDialog={setShowResumeDialog}
        />
      )}
    </dialog>
  );
};

export default ManageSubscriptionDialog;
